/**
 * Show mode: spectators spend fame to help or harass the hero they watch.
 * Effects apply pressure, never execution; costs escalate inside a realm and the target keeps half.
 */
export type CrowdKind = 'harm' | 'help';

export interface CrowdEffectDef {
  id: string;
  kind: CrowdKind;
  /** base fame price */
  cost: number;
  /** seconds before the same viewer may buy again */
  cooldown: number;
  glyph: string;
  color: string;
  /** effect payload interpreted by the room */
  apply:
    | { t: 'spawn'; unit: string; count: number; elite?: boolean }
    | { t: 'status'; id: string; duration: number; stacks?: number }
    | { t: 'boss' }
    | { t: 'corruption'; add: number }
    | { t: 'revenant' }
    | { t: 'heal'; pct: number }
    | { t: 'shield'; power: number; duration: number }
    | { t: 'gift'; consumable: string }
    | { t: 'loot' };
}

export const CROWD_EFFECTS: Record<string, CrowdEffectDef> = {
  // harm
  cr_pack: { id: 'cr_pack', kind: 'harm', cost: 60, cooldown: 12, glyph: '☠', color: '#c0304f', apply: { t: 'spawn', unit: 'ghoul', count: 5 } },
  cr_hounds: { id: 'cr_hounds', kind: 'harm', cost: 90, cooldown: 12, glyph: '🐺', color: '#c0304f', apply: { t: 'spawn', unit: 'hound', count: 4 } },
  cr_elite: { id: 'cr_elite', kind: 'harm', cost: 160, cooldown: 20, glyph: '★', color: '#ff9a3c', apply: { t: 'spawn', unit: 'bone_knight', count: 1, elite: true } },
  cr_slow: { id: 'cr_slow', kind: 'harm', cost: 80, cooldown: 15, glyph: '❄', color: '#7fdcff', apply: { t: 'status', id: 'chill', duration: 6, stacks: 4 } },
  cr_weak: { id: 'cr_weak', kind: 'harm', cost: 100, cooldown: 15, glyph: '↓', color: '#8a6a9a', apply: { t: 'status', id: 'weak', duration: 8 } },
  cr_fragile: { id: 'cr_fragile', kind: 'harm', cost: 120, cooldown: 15, glyph: '◇', color: '#c65cff', apply: { t: 'status', id: 'vulnerable', duration: 8 } },
  cr_corruption: { id: 'cr_corruption', kind: 'harm', cost: 140, cooldown: 30, glyph: '☣', color: '#c65cff', apply: { t: 'corruption', add: 15 } },
  cr_revenant: { id: 'cr_revenant', kind: 'harm', cost: 250, cooldown: 45, glyph: '👻', color: '#c8b8ff', apply: { t: 'revenant' } },
  cr_boss: { id: 'cr_boss', kind: 'harm', cost: 900, cooldown: 600, glyph: '♛', color: '#ff5c5c', apply: { t: 'boss' } },
  // help (deliberately small)
  cr_heal: { id: 'cr_heal', kind: 'help', cost: 40, cooldown: 12, glyph: '♥', color: '#7be0a0', apply: { t: 'heal', pct: 0.12 } },
  cr_shield: { id: 'cr_shield', kind: 'help', cost: 60, cooldown: 15, glyph: '◈', color: '#dfe9ff', apply: { t: 'shield', power: 0.25, duration: 6 } },
  cr_haste: { id: 'cr_haste', kind: 'help', cost: 60, cooldown: 15, glyph: '»', color: '#7fffd4', apply: { t: 'status', id: 'haste', duration: 5 } },
  cr_might: { id: 'cr_might', kind: 'help', cost: 80, cooldown: 15, glyph: '⚔', color: '#ffb35c', apply: { t: 'status', id: 'might', duration: 5 } },
  cr_potion: { id: 'cr_potion', kind: 'help', cost: 70, cooldown: 20, glyph: '⚗', color: '#ff5c7a', apply: { t: 'gift', consumable: 'potion_life' } },
  cr_loot: { id: 'cr_loot', kind: 'help', cost: 120, cooldown: 30, glyph: '◆', color: '#ffd166', apply: { t: 'loot' } },
};

export const CROWD_IDS = Object.keys(CROWD_EFFECTS);
/** share of spent fame credited to the watched hero's account */
export const CROWD_SHARE = 0.5;
/** price growth per purchase of the same effect against the same hero inside one realm */
export const CROWD_ESCALATION = 0.2;
/** max harm purchases per target per minute across all viewers */
export const CROWD_HARM_PER_MINUTE = 4;
/** fame the hero earns when a crowd-fed realm kills them */
export const CROWD_MARTYR_FAME = 60;
